import clsx from 'clsx';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import DashboardComponentHeader from 'components/dashboard/common/DashboardComponentHeader';
import ImageWithFallback from 'components/ImageWithFallback';
import { AvatarOnBeachTowel } from 'components/referrals/2026/AvatarOnBeachTowel';
import { AvatarOnRubberRing } from 'components/referrals/2026/AvatarOnRubberRing';
import {
  FriendIconBubbleContent,
  FriendIconIndex,
  FriendIconPlayer,
  getFriendIconTailPosition,
  isFriendIconInactive,
} from 'components/referrals/2026/FriendIconShared';
import { WhiteSpeechBubble } from 'components/referrals/2026/WhiteSpeechBubble';
import { referralsFolder } from 'constants/Referrals';
import type { PlayerInvitee } from 'hooks/referrals/usePlayerInvitesAndInvitees';
import { ReferralInviteProgressBar } from '../../referrals/2026/FriendsInvitedProgress';
import Beach1 from '../../svg/refer-a-friend/Beach1.svg';
import DashboardComponentLink from '../common/DashboardComponentLink';
import AvatarSpeechBubble from './AvatarSpeechBubble';
import styles from './YourReferrals.module.css';

interface FriendIconProps {
  player: FriendIconPlayer | null;
  index: FriendIconIndex;
}

const FriendIcon = ({ player, index }: FriendIconProps) => {
  return (
    <>
      <div
        className={clsx(
          styles.speechBubbleIcon,
          styles[`speechBubbleIcon${index}`],
        )}
      >
        <WhiteSpeechBubble
          empty={!player}
          tailPosition={getFriendIconTailPosition(index)}
        >
          <FriendIconBubbleContent player={player} />
        </WhiteSpeechBubble>
      </div>
      <div className={clsx(styles.friendIcon, styles['friendIcon' + index])}>
        <AvatarOnBeachTowel
          isEmptySlot={!player}
          avatarUrl={player?.avatarUrl}
          towelIndex={index}
          inactive={isFriendIconInactive(player)}
        />
      </div>
    </>
  );
};

interface YourReferralsProps {
  referees?: PlayerInvitee[];
  loading?: boolean;
  maxItems?: number;
  winsShared: number;
  referrerName?: string;
  referrerAvatar?: string;
}

const YourReferrals = ({
  referees: allReferees = [],
  loading = false,
  maxItems = 3,
  winsShared,
  referrerName,
  referrerAvatar,
}: YourReferralsProps) => {
  const friendsInvited = allReferees.length;

  const refereeSlots: (PlayerInvitee | null)[] = Array.from(
    { length: 3 },
    (_, index) => allReferees[index] || null,
  );

  return (
    <AboutUsSiteInnerContainer overflowHidden borderRadius positionRelative>
      <div className={styles.areaHeader}>
        <div className={styles.areaHeaderContent}>
          <DashboardComponentHeader
            title="Your referrals"
            subtitle={
              <>
                Win <strong>up to £5,000</strong> every time one of your friends
                wins a prize in our weekly draws
              </>
            }
            separator
            separatorColor="daymadeGrey1"
            loading={loading}
          />
        </div>
      </div>
      <div className={styles.contentStack}>
        <Beach1 className={styles.beach} />
        <div className={styles.refereeListContainer}>
          <AspectRatioContainer useCssVariableSizes>
            {refereeSlots.map((referee, index) => {
              return (
                <FriendIcon
                  key={index}
                  player={
                    referee
                      ? {
                          avatarUrl: referee.avatar ?? undefined,
                          name: referee.name,
                          status: referee.status,
                          gracePeriodHoursRemaining:
                            referee.gracePeriodHoursRemaining,
                        }
                      : null
                  }
                  index={(index + 1).toString() as FriendIconIndex}
                />
              );
            })}
          </AspectRatioContainer>
        </div>

        {referrerName && (
          <div className={styles.referredBy}>
            <div className={styles.referredByAvatar}>
              <AvatarOnRubberRing avatarUrl={referrerAvatar} />
            </div>
            <div className={styles.referrerBySpeechBubble}>
              <AvatarSpeechBubble>
                <p className="textAlignLeft textWhite">
                  You were referred by <strong>{referrerName}</strong>
                </p>
              </AvatarSpeechBubble>
            </div>
          </div>
        )}
        <div className={styles.referralFooter}>
          <div className={styles.referralStatSection}>
            <div className={styles.referralStat}>
              <div className={styles.referralStatHeader}>
                <div className={styles.referralStatIcon}>
                  <ImageWithFallback
                    layout="fill"
                    src={`${referralsFolder}/friends-invited.svg`}
                  />
                </div>
                <h3 className="aboutUsTypographyHeaderMedium noMargin">
                  Friends invited
                </h3>
                <h3
                  className={`aboutUsTypographyHeaderMedium ${styles.statValue}`}
                >
                  {friendsInvited}/{maxItems}
                </h3>
              </div>

              <ReferralInviteProgressBar
                invitedCount={friendsInvited}
                totalAllowed={maxItems}
              />
            </div>

            <div className={styles.referralStat}>
              <div className={styles.referralStatHeader}>
                <div className={styles.referralStatIcon}>
                  <ImageWithFallback
                    layout="fill"
                    src={`${referralsFolder}/wins-shared.svg`}
                  />
                </div>
                <p className="aboutUsTypographyHeaderMedium noMargin">
                  Wins shared
                </p>
                <p
                  className={`aboutUsTypographyHeaderMedium  ${styles.statValue}`}
                >
                  {winsShared}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.referralLink}>
            <DashboardComponentLink
              linkText="View referrals"
              href="/referrals"
            />
          </div>
        </div>
      </div>
    </AboutUsSiteInnerContainer>
  );
};

export default YourReferrals;
